import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { 
  DocumentTextIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CalendarIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { apiFetch, listParamHarnessRuns, type ParamHarnessRun } from '@/utils/api'
import { useAppStore } from '../store/appStore'
import { parseServerDate } from '@/utils/dates'

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Reports' },
  { value: 'completed', label: 'Completed' },
  { value: 'running', label: 'Running' },
  { value: 'failed', label: 'Failed' },
]

const formatCreatedAt = (timestamp?: string) => {
  const createdAtDate = timestamp ? parseServerDate(timestamp) : null
  return createdAtDate ? format(createdAtDate, 'MMM dd, HH:mm') : 'Unknown'
}

type PayloadLabStatsSummary = {
  attempts?: number | null
  signals?: number | null
  wafHits?: number | null
  lastAttemptAt?: string | null
}

const resolvePayloadLabSummary = (report: any): PayloadLabStatsSummary | null => {
  if (!report) return null
  const meta = report.metadata || (report.extractedData?.metadata ?? {}) || {}
  const candidates = [
    meta.payloadLabStats,
    meta.payload_lab_stats,
    meta.payloadLabSummary,
    meta.payload_lab_summary,
    meta.payloadLab,
    meta.payload_lab,
    report.payloadLabStats,
    report.payload_lab_stats,
    report.extractedData?.payloadLabStats,
    report.extracted_data?.payloadLabStats
  ]
  const stats = candidates.find((value) => value && typeof value === 'object') || null
  if (!stats) return null
  const attempts = stats.attempts ?? stats.total ?? stats.totalAttempts ?? stats.runs ?? null
  const signals = stats.signals ?? stats.signalsHit ?? stats.uniqueSignals ?? stats.evasions ?? null
  const wafHits = stats.wafHits ?? stats.waf ?? stats.wafIndicators ?? stats.waf_hits ?? null
  const lastAttemptAt = stats.lastAttemptAt ?? stats.last_run_at ?? stats.lastRunAt ?? stats.lastAttempt ?? null
  if (attempts == null && signals == null && wafHits == null && !lastAttemptAt) return null
  return { attempts, signals, wafHits, lastAttemptAt }
}

// Note: report shape comes from the API / store; use `any` when rendering to avoid strict shape mismatches.

export default function Reports() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [harnessRuns, setHarnessRuns] = useState<ParamHarnessRun[]>([])
  const [harnessLoading, setHarnessLoading] = useState(false)
  const [harnessError, setHarnessError] = useState<string | null>(null)

  const {
    removeReport,
    reports,
    reportsTotal,
    reportsLimit,
    reportsOffset,
    loadReports: storeLoadReports,
    setReportsLimit,
    setReportsOffset,
    setReportsQuery,
  } = useAppStore((s) => ({
    removeReport: s.removeReport,
    reports: s.reports,
    reportsTotal: s.reportsTotal,
    reportsLimit: s.reportsLimit,
    reportsOffset: s.reportsOffset,
    loadReports: s.loadReports,
    setReportsLimit: s.setReportsLimit,
    setReportsOffset: s.setReportsOffset,
    setReportsQuery: s.setReportsQuery,
  }))

  const currentUser = useAppStore((s) => s.currentUser)
  const isAdmin = currentUser?.role === 'admin'

  const [harnessSelection, setHarnessSelection] = useState<string>('recent')

  // Debounce search input and pass to server (store)
  useEffect(() => {
    const t = setTimeout(() => {
      // Push query to store which will reload reports
      setReportsQuery(searchTerm.trim())
    }, 400)
    return () => clearTimeout(t)
  }, [searchTerm, setReportsQuery])

  // When status filter changes, reload from server using current query
  useEffect(() => {
    ;(async () => {
      try {
        await storeLoadReports({ status: statusFilter })
      } catch (e) {
        // ignore
      }
    })()
  }, [statusFilter, storeLoadReports])

  const loadReports = async () => {
    setLoading(true)
    setError(null)
    try {
      await storeLoadReports()
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : err?.toString() || 'Failed to load reports'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const loadHarnessRuns = useCallback(async () => {
    const scopedReportId = harnessSelection === 'recent' ? null : harnessSelection || null
    if (!isAdmin && !scopedReportId) {
      setHarnessRuns([])
      setHarnessError('Run a scan to generate a report before viewing harness evidence.')
      return
    }

    setHarnessLoading(true)
    setHarnessError(null)
    try {
      const query: { limit: number; reportId?: string } = { limit: 5 }
      if (scopedReportId) {
        query.reportId = scopedReportId
      }
      const res = await listParamHarnessRuns(query)
      setHarnessRuns(res?.runs || [])
    } catch (err: any) {
      const message = err instanceof Error ? err.message : err?.toString() || 'Failed to load harness previews'
      setHarnessError(message)
      toast.error(message)
    } finally {
      setHarnessLoading(false)
    }
  }, [harnessSelection, isAdmin])

  useEffect(() => {
    // Load initial page from the store
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        await storeLoadReports()
      } catch (err: any) {
        setError(err?.message || 'Failed to load reports')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  useEffect(() => {
    loadHarnessRuns()
  }, [loadHarnessRuns])

  const downloadReport = async (reportId: string, format: string = 'json') => {
    try {
      // For downloads, we still need to attach Authorization; use a manual fetch with header
      const token = localStorage.getItem('authToken')
      const response = await fetch(`/api/reports/${reportId}/export/${format}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      if (!response.ok) throw new Error('Failed to download report')
      const isPdfFallback = response.headers.get('x-pdf-fallback') === 'true'
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ext = isPdfFallback ? 'html' : format
      a.download = `report-${reportId}.${ext}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      toast.success(`Report downloaded as ${ext.toUpperCase()}`)
    } catch {
      toast.error('Failed to download report')
    }
  }

  const deleteReport = async (reportId: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this report? This action cannot be undone.')
    if (!confirmed) return

    try {
      await apiFetch(`/api/reports/${reportId}`, {
        method: 'DELETE'
      })
      
      // Remove the report from the store (this will update Dashboard)
      removeReport(reportId)
      
  // local reports state is the store; `removeReport` already updated the store
      
      toast.success('Report deleted successfully')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete report'
      toast.error(errorMessage)
    }
  }

  // Server-side search is used; apply only status filter client-side as a fallback
  const displayedReports = reports.filter(report => {
    const matchesStatus = statusFilter === 'all' || report.status === statusFilter
    return matchesStatus
  })

  const reportOptions = useMemo<Array<{ value: string; label: string }>>(() => {
    return (reports || []).map((report: any) => ({
      value: String(report.id),
      label: report.title
        ? `${report.title} • ${report.target || 'Unknown target'}`
        : report.target || `Report ${report.id}`
    }))
  }, [reports])

  const harnessScopeOptions = useMemo<Array<{ value: string; label: string }>>(() => {
    if (isAdmin) {
      return [{ value: 'recent', label: 'All accessible targets' }, ...reportOptions]
    }
    return reportOptions
  }, [isAdmin, reportOptions])

  useEffect(() => {
    if (isAdmin) {
      if (harnessSelection === '') {
        setHarnessSelection('recent')
        return
      }
      if (harnessSelection && harnessSelection !== 'recent') {
        const exists = reportOptions.some((option) => option.value === harnessSelection)
        if (!exists) {
          setHarnessSelection('recent')
        }
      }
      return
    }

    if (!reportOptions.length) {
      if (harnessSelection !== '') {
        setHarnessSelection('')
      }
      return
    }

    const exists = reportOptions.some((option) => option.value === harnessSelection)
    if (!exists || harnessSelection === 'recent' || !harnessSelection) {
      setHarnessSelection(reportOptions[0].value)
    }
  }, [isAdmin, reportOptions, harnessSelection])

  // Pagination helpers
  const pageCount = Math.max(1, Math.ceil((reportsTotal || 0) / Math.max(1, reportsLimit)))
  const currentPage = Math.floor((reportsOffset || 0) / Math.max(1, reportsLimit)) + 1
  const buildPageList = () => {
    const pages: number[] = []
    if (pageCount <= 11) {
      for (let i = 1; i <= pageCount; i++) pages.push(i)
      return pages
    }
    // Always include first, last, and 2 neighbours around current
    pages.push(1)
    let start = Math.max(2, currentPage - 2)
    let end = Math.min(pageCount - 1, currentPage + 2)
    if (start > 2) pages.push(-1) // ellipsis marker
    for (let i = start; i <= end; i++) pages.push(i)
    if (end < pageCount - 1) pages.push(-1)
    pages.push(pageCount)
    return pages
  }

  const renderReportActions = (report: any, align: 'start' | 'end' = 'end') => (
    <div className={clsx(
      'flex flex-wrap items-center gap-2 text-sm font-medium',
      align === 'end' ? 'justify-end' : 'justify-start'
    )}>
      <Link
        to={`/reports/${report.id}`}
        className="text-blue-400 hover:text-blue-300 transition-colors"
        title="View Report"
      >
        <EyeIcon className="h-4 w-4" />
      </Link>
      
      <button
        onClick={() => deleteReport(report.id)}
        className="text-red-400 hover:text-red-300 transition-colors"
        title="Delete Report"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
      
      <div className="relative group">
        <button 
          className="text-green-400 hover:text-green-300 transition-colors"
          title="Download Report"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
        </button>
        
        <div className="absolute right-0 mt-2 w-36 bg-gray-700 border border-gray-600 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
          <div className="py-1">
            {String(import.meta.env.VITE_ENABLE_PDF_EXPORT || 'false').toLowerCase() === 'true' && (
              <button
                onClick={() => downloadReport(report.id, 'pdf')}
                className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-600 hover:text-white"
              >
                Download PDF
              </button>
            )}
            <button
              onClick={() => downloadReport(report.id, 'html')}
              className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-600 hover:text-white"
            >
              Download HTML
            </button>
            <button
              onClick={() => downloadReport(report.id, 'json')}
              className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-600 hover:text-white"
            >
              Download JSON
            </button>
            <button
              onClick={() => downloadReport(report.id, 'csv')}
              className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-600 hover:text-white"
            >
              Download CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  const renderPayloadLabBadges = (summary: PayloadLabStatsSummary | null) => {
    if (!summary) {
      return <span className="text-xs text-gray-500">Payload lab pending</span>
    }
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-300">
        {summary.attempts != null && (
          <span className="inline-flex items-center gap-1 rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2 py-0.5 text-indigo-100">
            {summary.attempts} attempts
          </span>
        )}
        {summary.signals != null && (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-emerald-100">
            {summary.signals} signals
          </span>
        )}
        {summary.wafHits != null && (
          <span className="inline-flex items-center gap-1 rounded-full border border-orange-500/40 bg-orange-500/10 px-2 py-0.5 text-orange-100">
            {summary.wafHits} WAF hits
          </span>
        )}
        {summary.lastAttemptAt && (
          <span className="text-gray-500">Updated {formatCreatedAt(summary.lastAttemptAt)}</span>
        )}
      </div>
    )
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-400" />
      case 'running':
        return <ClockIcon className="h-5 w-5 text-yellow-400 animate-spin" />
      case 'failed':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400 bg-green-400/10'
      case 'running':
        return 'text-yellow-400 bg-yellow-400/10'
      case 'failed':
        return 'text-red-400 bg-red-400/10'
      default:
        return 'text-gray-400 bg-gray-400/10'
    }
  }

  const getSeverityColor = (riskLevel: string) => {
    switch (riskLevel?.toLowerCase()) {
      case 'critical':
      case 'high':
        return 'text-red-400 bg-red-400/10'
      case 'medium':
        return 'text-yellow-400 bg-yellow-400/10'
      case 'low':
        return 'text-blue-400 bg-blue-400/10'
      default:
        return 'text-gray-400 bg-gray-400/10'
    }
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-400">Loading reports...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Error Loading Reports</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={loadReports}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-white flex items-center text-balance">
                <DocumentTextIcon className="h-8 w-8 mr-3 text-blue-400" />
                Scan Reports
              </h1>
              <p className="text-gray-400 text-balance max-w-2xl">
                View and manage your security scan reports ({reportsTotal ?? reports.length} total)
              </p>
            </div>
            <button
              onClick={loadReports}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by target URL or title..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label htmlFor="statusFilter" className="sr-only">Filter status</label>
              <select
                id="statusFilter"
                title="Filter status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <HarnessPreviewPanel
          runs={harnessRuns}
          loading={harnessLoading}
          error={harnessError}
          onReload={loadHarnessRuns}
          scopeValue={harnessSelection}
          onScopeChange={setHarnessSelection}
          scopeOptions={harnessScopeOptions}
          scopeDisabled={!isAdmin && reportOptions.length === 0}
          showScopeSelect={isAdmin || reportOptions.length > 0}
        />

        <div className="bg-gray-800 rounded-lg border border-gray-700">
          <div className="px-6 py-4 border-b border-gray-700">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h3 className="text-lg font-medium text-white">
                {displayedReports.length === 0 && reports.length > 0 ? 'No reports match your filters' : 'Recent Reports'}
              </h3>

              <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm text-gray-400">Per page</label>
                <select
                  title="Per page"
                  aria-label="Reports per page"
                  value={reportsLimit}
                  onChange={(e) => setReportsLimit(Number(e.target.value))}
                  className="px-2 py-1 bg-gray-700 border border-gray-600 rounded-md text-sm text-white"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>

                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
                  <button
                    onClick={() => setReportsOffset(Math.max(0, reportsOffset - reportsLimit))}
                    disabled={reportsOffset <= 0}
                    className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-md disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setReportsOffset(Math.min(reportsOffset + reportsLimit, Math.max(0, (Math.ceil((reportsTotal || 0) / reportsLimit) - 1) * reportsLimit)))}
                    disabled={reportsOffset + reportsLimit >= (reportsTotal || 0)}
                    className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-md disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {displayedReports.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">
                {reports.length === 0 ? 'No reports available yet' : 'No reports match your search criteria'}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {reports.length === 0 ? 'Run some scans to generate reports' : 'Try adjusting your search or filters'}
              </p>
            </div>
          ) : (
            <>
              <div className="md:hidden px-4 py-4 space-y-4">
                {displayedReports.map((report: any) => {
                  const payloadSummary = resolvePayloadLabSummary(report)
                  return (
                    <div key={report.id} className="rounded-lg border border-gray-700 bg-gray-900 p-4 space-y-4">
                    <div className="space-y-1">
                      <div className="text-sm font-semibold text-white break-words text-balance">
                        {report.target}
                      </div>
                      <div className="text-xs text-gray-400 break-words text-balance">
                        {report.title}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {getStatusIcon(report.status)}
                      <span className={clsx(
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        getStatusColor(report.status)
                      )}>
                        {report.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-300">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4 text-gray-400" />
                        {formatCreatedAt(report.created_at)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">
                          {report.vulnerabilities?.total || 0}
                        </span>
                        {report.vulnerabilities?.riskLevel && (
                          <span className={clsx(
                            'inline-flex items-center px-2 py-1 rounded text-xs font-medium',
                            getSeverityColor(report.vulnerabilities.riskLevel)
                          )}>
                            {report.vulnerabilities.riskLevel}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="pt-2 border-t border-gray-800">
                      {renderPayloadLabBadges(payloadSummary)}
                    </div>
                    {renderReportActions(report, 'start')}
                  </div>
                  )
                })}
              </div>
              <div className="hidden md:block overflow-x-auto -mx-6 sm:mx-0">
              <table className="w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider break-words">
                      Target
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider break-words">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider break-words">
                      Vulnerabilities
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider break-words">
                      Created
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider break-words">
                      Payload Lab
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider break-words">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {displayedReports.map((report: any) => {
                    const payloadSummary = resolvePayloadLabSummary(report)
                    return (
                      <tr key={report.id} className="hover:bg-gray-700">
                        <td className="px-4 py-4 align-top">
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-white break-words text-balance">
                              {report.target}
                            </div>
                            <div className="text-xs text-gray-400 break-words text-balance">
                              {report.title}
                            </div>
                          </div>
                        </td>
                      
                        <td className="px-4 py-4 align-top">
                          <div className="flex flex-wrap items-center gap-2">
                            {getStatusIcon(report.status)}
                            <span className={clsx(
                              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                              getStatusColor(report.status)
                            )}>
                              {report.status}
                            </span>
                          </div>
                        </td>
                      
                        <td className="px-4 py-4 align-top">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-white">
                              {report.vulnerabilities?.total || 0}
                            </span>
                            {report.vulnerabilities?.riskLevel && (
                              <span className={clsx(
                                'inline-flex items-center px-2 py-1 rounded text-xs font-medium',
                                getSeverityColor(report.vulnerabilities.riskLevel)
                              )}>
                                {report.vulnerabilities.riskLevel}
                              </span>
                            )}
                          </div>
                        </td>
                      
                        <td className="px-4 py-4 align-top text-sm text-gray-400">
                          <div className="flex flex-wrap items-center gap-2">
                            <CalendarIcon className="h-4 w-4 text-gray-400" />
                            {formatCreatedAt(report.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top">
                          {renderPayloadLabBadges(payloadSummary)}
                        </td>
                        
                        <td className="px-4 py-4 align-top text-right text-sm font-medium">
                          {renderReportActions(report)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              </div>
              {/* Pagination controls */}
              <div className="px-6 py-4 border-t border-gray-700 bg-gray-800 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-gray-400">
                Showing {(reportsOffset || 0) + 1} - {Math.min((reportsOffset || 0) + reportsLimit, reportsTotal || reports.length)} of {reportsTotal ?? reports.length}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {buildPageList().map((p, idx) => (
                  p === -1 ? (
                    <span key={`ell-${idx}`} className="px-2 text-gray-500">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setReportsOffset((p - 1) * reportsLimit)}
                      aria-current={p === currentPage ? 'page' : undefined}
                      className={clsx('px-3 py-1 rounded-md text-sm', p === currentPage ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600')}
                    >
                      {p}
                    </button>
                  )
                ))}
              </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

type HarnessPreviewPanelProps = {
  runs: ParamHarnessRun[]
  loading: boolean
  error: string | null
  onReload: () => void
  scopeValue: string
  onScopeChange: (value: string) => void
  scopeOptions: Array<{ value: string; label: string }>
  scopeDisabled?: boolean
  showScopeSelect?: boolean
}

function HarnessPreviewPanel({
  runs,
  loading,
  error,
  onReload,
  scopeValue,
  onScopeChange,
  scopeOptions,
  scopeDisabled,
  showScopeSelect = true
}: HarnessPreviewPanelProps) {
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Latest Parameter Harness Runs</h3>
          <p className="text-sm text-gray-400">
            Deterministic probes captured before SQLMap provide early evidence and WAF hints.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {showScopeSelect && (
            <div className="flex flex-col">
              <label className="text-xs uppercase tracking-wide text-gray-400">Scope</label>
              <select
                title="Harness scope"
                value={scopeValue}
                onChange={(e) => onScopeChange(e.target.value)}
                disabled={scopeDisabled || scopeOptions.length === 0}
                className="min-w-[200px] px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
              >
                {scopeOptions.length === 0 ? (
                  <option value="">No reports available</option>
                ) : (
                  scopeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                )}
              </select>
            </div>
          )}
          <button
            onClick={onReload}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>
      {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
      <div className="mt-4 space-y-4">
        {loading ? (
          <div className="space-y-3 animate-pulse">
            {[0, 1, 2].map((idx) => (
              <div key={idx} className="h-16 bg-gray-700/40 rounded-md" />
            ))}
          </div>
        ) : runs.length === 0 ? (
          <p className="text-sm text-gray-400">No harness runs recorded yet. Launch a scan to capture baselines.</p>
        ) : (
          runs.map((run) => {
            const statusDelta = `${run.status_control ?? '—'} → ${run.status_payload ?? '—'}`
            const timeDelta =
              run.time_control != null && run.time_payload != null
                ? `${run.time_control}ms → ${run.time_payload}ms`
                : '—'
            const reflectionValue =
              typeof run.reflection_hits === 'number'
                ? run.reflection_hits > 0
                  ? `Reflected x${run.reflection_hits}`
                  : 'No reflection'
                : 'Unknown'
            const diffChars =
              typeof run.diff_length === 'number'
                ? `${run.diff_length > 0 ? '+' : ''}${run.diff_length} chars`
                : '—'
            const payloadHash = run.metadata?.preview_payload?.hash
              ? run.metadata.preview_payload.hash.slice(0, 12)
              : '—'

            return (
              <div key={run.id} className="border border-gray-700 rounded-lg p-4 bg-gray-900/60">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-white">
                      {run.parameter_name || run.parameter_id || 'Unknown parameter'}
                    </p>
                    <p className="text-xs text-gray-400 break-all">{run.target || 'Unknown target'}</p>
                  </div>
                  <div className="text-xs text-gray-400">
                    {run.created_at ? formatCreatedAt(run.created_at) : 'Unknown time'}
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <HarnessStat label="Status" value={statusDelta} />
                  <HarnessStat label="Latency" value={timeDelta} />
                  <HarnessStat label="Reflection" value={reflectionValue} />
                  <HarnessStat label="Diff" value={diffChars} />
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-gray-400">
                  <div className="bg-gray-800/80 rounded-md p-3 border border-gray-700/60">
                    <p className="uppercase text-[11px] tracking-wide text-gray-500 mb-1">Request</p>
                    <p className="text-gray-200 break-all">
                      {run.method || 'GET'} · {run.url || 'Unknown URL'}
                    </p>
                  </div>
                  <div className="bg-gray-800/80 rounded-md p-3 border border-gray-700/60">
                    <p className="uppercase text-[11px] tracking-wide text-gray-500 mb-1">Payload hash</p>
                    <p className="text-gray-200 break-all">{payloadHash}</p>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

type HarnessStatProps = {
  label: string
  value: string
}

function HarnessStat({ label, value }: HarnessStatProps) {
  return (
    <div className="bg-gray-900/70 border border-gray-700/60 rounded-md p-3">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-sm font-semibold text-white mt-1 break-words">{value}</p>
    </div>
  )
}
